import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Image,
  Link,
  Stack,
} from '@chakra-ui/react';
import AnnouncementResource from 'api/announcement';
import { CenterSpinner } from 'components/common/CenterSpinner';
import { strings } from 'config/localization';
import routes from 'constants/routes';
import { AnnouncementSchema } from 'constants/schema';
import React from 'react';
import { useQuery } from 'react-query';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { truncateString } from 'utils';
import history from 'utils/history';

const ViewAnnouncement: React.FC = () => {
  const { id }: any = useParams();
  const announcementApi = new AnnouncementResource();

  const announcementQuery = useQuery<AnnouncementSchema>(
    [`announcement${id}`, id],
    () => announcementApi.get(id).then((res) => res?.data),
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  if (announcementQuery.isLoading) {
    return <CenterSpinner />;
  }

  if (announcementQuery.isError) {
    history.push(routes.announcements.list);
  }

  const labelStyles = {
    color: 'gray.900',
    fontWeight: 'bold',
  };
  const infoStyles = {
    color: 'gray.600',
    fontWeight: 'normal',
  };

  return (
    <>
      <Stack direction="column" spacing="4">
        <Breadcrumb color="gray.400" size="4">
          <BreadcrumbItem>
            <BreadcrumbLink as={RouterLink} to={routes.announcements.list}>
              {strings.announcement}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage color="gray.900">
            <BreadcrumbLink textTransform="capitalize">
              {truncateString(announcementQuery?.data?.title || '')}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        <Flex justify="space-between">
          <Heading size="md" textTransform="capitalize">
            {strings.announcement_details}
          </Heading>
          <Stack direction="row" spacing="6">
            <Button
              size="sm"
              colorScheme="primary"
              onClick={() =>
                history.push(routes.announcements.edit.replace(':id', id))
              }>
              {strings.edit_announcement}
            </Button>
          </Stack>
        </Flex>

        <Stack bg="white" p={['3', '6']} shadow="box" rounded="sm">
          <Grid
            gap="4"
            templateColumns={['repeat(2, 1fr)', 'repeat(4, 1fr)']}
            w="100%">
            <GridItem>
              <FormControl>
                <FormLabel sx={labelStyles}>{strings.title}</FormLabel>
                <Box sx={infoStyles}>
                  {announcementQuery?.data?.title || '-'}
                </Box>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel sx={labelStyles}>{strings.link}</FormLabel>
                <Box sx={infoStyles}>
                  <Link href={announcementQuery?.data?.link} isExternal>
                    {announcementQuery?.data?.link || '-'}
                  </Link>
                </Box>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel sx={labelStyles}>{strings.description}</FormLabel>
                <Box sx={infoStyles}>
                  {announcementQuery?.data?.description || '-'}
                </Box>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel sx={labelStyles}>{strings.written_by}</FormLabel>
                <Box sx={infoStyles}>
                  {announcementQuery?.data?.written_by || '-'}
                </Box>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel sx={labelStyles}>{strings.company_feed}</FormLabel>
                <Box sx={infoStyles}>
                  {announcementQuery?.data?.company_feed ? 'Yes' : 'No'}
                </Box>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel sx={labelStyles}>{strings.jobsite_feed}</FormLabel>
                <Box sx={infoStyles}>
                  {announcementQuery?.data?.jobsite_feed ? 'Yes' : 'No'}
                </Box>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel sx={labelStyles}>{strings.all_user}</FormLabel>
                <Box sx={infoStyles}>
                  {announcementQuery?.data?.all_users ? 'Yes' : 'No'}
                </Box>
              </FormControl>
            </GridItem>
          </Grid>
          {announcementQuery?.data?.image && (
            <Grid
              gap="4"
              templateColumns={['repeat(2, 1fr)', 'repeat(4, 1fr)']}
              w="100%">
              <GridItem>
                <FormControl>
                  <FormLabel sx={labelStyles}>{strings.image}</FormLabel>
                  <Box sx={infoStyles}>
                    <Image
                      boxSize="150px"
                      src={announcementQuery?.data?.image}
                      alt={announcementQuery?.data?.title}
                    />
                  </Box>
                </FormControl>
              </GridItem>
            </Grid>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default ViewAnnouncement;
